import React, { useState } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { fmtUsd } from "@/lib/journal";

const COLORS = ["#C58361", "#A86B4C", "#E2A882", "#7C5138", "#D9C3B0", "#8A6350", "#F0D3BE"];

export const AllocationChart = ({ data }) => {
  const [mode, setMode] = useState("by_asset");
  const slices = data?.[mode] || [];
  const total = data?.total_value || 0;

  return (
    <div className="border border-[#1F1F1F] bg-surface p-6" data-testid="allocation-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Open Book Allocation</h3>
        <div className="flex gap-2">
          {[
            { id: "by_asset", label: "By Asset" },
            { id: "by_portfolio", label: "By Portfolio" },
          ].map((m) => (
            <button
              key={m.id}
              data-testid={`allocation-mode-${m.id}`}
              onClick={() => setMode(m.id)}
              className={`border px-2.5 py-1 text-[10px] uppercase tracking-widest transition-colors duration-200 ${
                mode === m.id
                  ? "border-[#C58361] bg-[#C58361]/10 text-[#C58361]"
                  : "border-[#1F1F1F] text-zinc-500 hover:text-zinc-200"
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>

      {slices.length === 0 ? (
        <p className="mt-8 border border-dashed border-[#1F1F1F] px-4 py-12 text-center text-sm text-zinc-600">
          No open positions to allocate yet.
        </p>
      ) : (
        <div className="mt-6 grid items-center gap-6 sm:grid-cols-2">
          <div className="relative h-[220px] min-h-[220px]">
            <ResponsiveContainer width="100%" height={220} minWidth={0} minHeight={220}>
              <PieChart>
                <Pie
                  data={slices}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={62}
                  outerRadius={95}
                  paddingAngle={2}
                  stroke="#050505"
                  strokeWidth={2}
                >
                  {slices.map((s, i) => (
                    <Cell key={s.name} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(v) => fmtUsd(v)}
                  contentStyle={{ background: "#0A0A0A", border: "1px solid #1F1F1F", borderRadius: 2, fontFamily: "JetBrains Mono", fontSize: 12 }}
                  itemStyle={{ color: "#C58361" }}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-600">Open Value</span>
              <span className="font-mono text-lg text-white">{fmtUsd(total)}</span>
            </div>
          </div>

          <div className="space-y-3" data-testid="allocation-legend">
            {slices.map((s, i) => (
              <div key={s.name} className="flex items-center gap-3" data-testid={`allocation-row-${s.name}`}>
                <span className="h-2 w-2 shrink-0" style={{ background: COLORS[i % COLORS.length] }} />
                <span className="flex-1 truncate text-sm text-zinc-300">{s.name}</span>
                <span className="font-mono text-xs text-zinc-500">{s.pct}%</span>
                <span className="w-24 text-right font-mono text-sm text-white">{fmtUsd(s.value)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
